import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  getAccountingActivity,
  getAccountingDocumentRequests,
  saveAccountingDocumentRequestAnswers,
} from "../../api/accounting";
import { useAuth } from "../../context/AuthContext";
import { getSalonTimezone } from "../../utils/salonTimezone";
import { monthValueFromDate } from "./accountingDate";
import SalonBusinessCpaShell from "./SalonBusinessCpaShell";

const statusClasses = {
  needed: "border-amber-200 bg-amber-50 text-amber-700",
  received: "border-emerald-200 bg-emerald-50 text-emerald-700",
  being_reviewed: "border-sky-200 bg-sky-50 text-sky-700",
  needs_new_copy: "border-rose-200 bg-rose-50 text-rose-700",
  done: "border-slate-200 bg-slate-100 text-slate-700",
  cancelled: "border-slate-200 bg-slate-100 text-slate-700",
};

function formatDate(value) {
  if (!value) return "";
  const [year, month, day] = String(value).split("-").map(Number);
  const date = new Date(year, (month || 1) - 1, day || 1);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function periodText(request, timeZone) {
  return (
    request.period_label ||
    monthValueFromDate(request.period_start || request.due_date || request.created_at_utc, timeZone)
  );
}

function uploadPath(request) {
  const params = new URLSearchParams({
    document_type: request.document_type,
    request_id: String(request.id),
  });
  return `/salonbusinesscpa/documents?${params.toString()}`;
}

export default function SalonBusinessCpaRequests() {
  const { token, currentSalon, user } = useAuth();
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon || { salon_timezone: user?.salon_timezone }),
    [currentSalon, user?.salon_timezone]
  );
  const [requests, setRequests] = useState([]);
  const [activity, setActivity] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [monthFilter, setMonthFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [answerDrafts, setAnswerDrafts] = useState({});
  const [savingAnswersId, setSavingAnswersId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const filteredRequests = useMemo(() => {
    return requests.filter((request) => {
      if (statusFilter && request.status !== statusFilter) return false;
      if (typeFilter && request.document_type !== typeFilter) return false;
      if (monthFilter) {
        const values = [
          request.period_start,
          request.period_end,
          request.due_date,
          request.created_at_utc,
        ].map((value) => monthValueFromDate(value, salonTimezone));
        if (!values.includes(monthFilter)) return false;
      }
      return true;
    });
  }, [monthFilter, requests, salonTimezone, statusFilter, typeFilter]);

  const openRequests = useMemo(
    () => filteredRequests.filter((request) => !["done", "cancelled"].includes(request.status)),
    [filteredRequests]
  );

  const activityByRequest = useMemo(() => {
    return activity.reduce((next, event) => {
      if (!event.request_id) return next;
      const key = String(event.request_id);
      next[key] = next[key] || [];
      next[key].push(event);
      return next;
    }, {});
  }, [activity]);

  const loadRequests = async () => {
    setLoading(true);
    setError("");
    try {
      const [data, activityData] = await Promise.all([
        getAccountingDocumentRequests(token),
        getAccountingActivity(token, { limit: 120 }).catch(() => null),
      ]);
      setRequests(Array.isArray(data?.requests) ? data.requests : []);
      setAnswerDrafts(
        (Array.isArray(data?.requests) ? data.requests : []).reduce((next, request) => {
          if (!Array.isArray(request.answers) || !request.answers.length) return next;
          next[String(request.id)] = request.answers.reduce((answers, item) => {
            answers[item.code] = item.answer || "";
            return answers;
          }, {});
          return next;
        }, {})
      );
      setStatusOptions(Array.isArray(data?.status_options) ? data.status_options : []);
      setTypeOptions(Array.isArray(data?.document_type_options) ? data.document_type_options : []);
      setActivity(Array.isArray(activityData?.events) ? activityData.events : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load document requests.");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (requestId, code, value) => {
    setAnswerDrafts((current) => ({
      ...current,
      [String(requestId)]: {
        ...(current[String(requestId)] || {}),
        [code]: value,
      },
    }));
  };

  const handleSaveAnswers = async (request) => {
    setSavingAnswersId(request.id);
    setError("");
    try {
      const updated = await saveAccountingDocumentRequestAnswers(token, request.id, {
        answers: answerDrafts[String(request.id)] || {},
      });
      setRequests((current) =>
        current.map((item) => (item.id === request.id ? updated : item))
      );
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save these answers.");
    } finally {
      setSavingAnswersId(null);
    }
  };

  useEffect(() => {
    if (token) {
      loadRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <SalonBusinessCpaShell
      title="Document requests"
      description="See records your accountant needs, why they matter, and what to upload next."
      actions={
        <button
          type="button"
          onClick={loadRequests}
          className="rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Refresh
        </button>
      }
    >
      {error ? (
        <div className="rounded-3xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-950">
              Open requests
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Upload these records first so review can keep moving.
            </p>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
            {openRequests.length} open
          </span>
        </div>
        <div className="mt-5 grid gap-3 md:grid-cols-3">
          <label className="text-sm font-semibold text-slate-700">
            Month
            <input
              type="month"
              value={monthFilter}
              onChange={(event) => setMonthFilter(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 px-3 text-sm text-slate-700"
            />
          </label>
          <label className="text-sm font-semibold text-slate-700">
            Status
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm text-slate-700"
            >
              <option value="">All statuses</option>
              {statusOptions.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-semibold text-slate-700">
            Record type
            <select
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm text-slate-700"
            >
              <option value="">All types</option>
              {typeOptions.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="space-y-3">
        {loading ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
            Loading requests...
          </div>
        ) : filteredRequests.length ? (
          filteredRequests.map((request) => (
            <article
              key={request.id}
              className={`rounded-3xl border p-5 shadow-sm ${
                request.is_overdue
                  ? "border-amber-200 bg-amber-50"
                  : "border-slate-200 bg-white"
              }`}
            >
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-slate-950">
                      {request.title}
                    </h3>
                    <span
                      className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                        statusClasses[request.status] ||
                        "border-slate-200 bg-slate-100 text-slate-700"
                      }`}
                    >
                      {request.status_label}
                    </span>
                    {request.is_overdue ? (
                      <span className="rounded-full border border-amber-200 bg-white px-3 py-1 text-xs font-semibold text-amber-700">
                        Please send when ready
                      </span>
                    ) : null}
                    {periodText(request, salonTimezone) ? (
                      <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                        {periodText(request, salonTimezone)}
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    {request.client_note ||
                      "Please upload this record so your accounting review can continue."}
                  </p>
                  {Array.isArray(request.questions) && request.questions.length ? (
                    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-sm font-semibold text-slate-950">
                        Quick questions
                      </p>
                      <div className="mt-3 space-y-3">
                        {request.questions.map((question) => (
                          <label
                            key={question.code}
                            className="block text-sm font-semibold text-slate-700"
                          >
                            {question.label}
                            <input
                              value={
                                answerDrafts[String(request.id)]?.[question.code] || ""
                              }
                              onChange={(event) =>
                                handleAnswerChange(
                                  request.id,
                                  question.code,
                                  event.target.value
                                )
                              }
                              className="mt-2 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-sm font-normal text-slate-700"
                              placeholder="Short answer"
                            />
                          </label>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleSaveAnswers(request)}
                        disabled={savingAnswersId === request.id}
                        className="mt-4 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                      >
                        {savingAnswersId === request.id ? "Saving..." : "Save answers"}
                      </button>
                    </div>
                  ) : null}
                  <p className="mt-2 text-xs font-semibold text-slate-500">
                    Upload type: {request.document_type_label}
                    {request.due_date ? ` / Requested by ${formatDate(request.due_date)}` : ""}
                  </p>
                </div>
                {!["done", "cancelled"].includes(request.status) ? (
                  <Link
                    to={uploadPath(request)}
                    className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-full bg-rose-500 px-4 text-sm font-semibold text-white hover:bg-rose-600"
                  >
                    Upload this record
                  </Link>
                ) : null}
              </div>
              {activityByRequest[String(request.id)]?.length ? (
                <div className="mt-4 border-t border-slate-200 pt-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                    Request history
                  </p>
                  <ol className="mt-3 space-y-3">
                    {activityByRequest[String(request.id)].slice(0, 5).map((event) => (
                      <li key={event.id} className="flex gap-3 text-sm">
                        <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-rose-500" />
                        <span>
                          <span className="font-semibold text-slate-800">
                            {event.title}
                          </span>
                          {event.body ? (
                            <span className="text-slate-600"> - {event.body}</span>
                          ) : null}
                          <span className="block text-xs text-slate-500">
                            {formatDateTime(event.occurred_at_utc)}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              ) : null}
            </article>
          ))
        ) : (
          <div className="rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
            No document requests yet.
          </div>
        )}
      </section>
    </SalonBusinessCpaShell>
  );
}
